/**
 * Custom Site Specific Javascript
 * Port of Seattle Data Breach Settlement — portdatasettlement.com
 */

import { modal } from 'commonAssets/lib/modal';
import { toggleFieldError } from 'commonAssets/lib/toggleFieldError';

export function customInitClaim(claim) {
    console.log("[Initiate claim]...");

    _initBenefitValidation();
    _initAlternateEmailToggle();

    return claim;
}

export function customLoadMember(claim) {
    console.log("[Load class member]...");

    _lockNameFields();

    return claim;
}

export function customProcessClaim(claim) {
    console.log("[Process claim]...");

    if (!_validateBenefitSelection()) {
        claim.flags.valid = false;
        return claim;
    }

    if (!_validateBenefitARequirements(claim)) {
        claim.flags.valid = false;
        return claim;
    }

    if (!_validateAlternateEmail()) {
        claim.flags.valid = false;
        return claim;
    }

    return claim;
}

/**
 * Disable name fields so pre-populated values cannot be edited.
 */
function _lockNameFields() {
    const nameFields = [
        "first_name",
        "last_name",
        "middle_initial",
        "suffix",
    ];

    nameFields.forEach((fieldName) => {
        const field = document.querySelector(`[name="${fieldName}"]`);
        if (field && field.value) {
            field.setAttribute("readonly", true);
            field.classList.add("bg-light");
        }
    });
}

/**
 * Set up benefit checkbox change listeners.
 */
function _initBenefitValidation() {
    const benefitA = document.querySelector(
        '[name="benefit_a_oop_losses"]'
    );
    const benefitB = document.querySelector(
        '[name="benefit_b_pro_rata"]'
    );
  
    const transactionsSection = document.querySelector(
        '#transactions'
    );
    const documentationSection = document.querySelector(
        '#documentation'
    );

    // by default transactions and documentation sections are hidden
    transactionsSection.classList.toggle("d-none", !benefitA.checked);
    documentationSection.classList.toggle("d-none", !benefitA.checked);

    // Show or hide Benefit A sub-fields based on the current checked state.
    const benefitAFields = document.querySelectorAll(".toggle-benefit_a_oop_losses");
    benefitAFields.forEach((el) => {
        el.parentElement.classList.toggle("d-none", !benefitA.checked);
    });

    if (benefitA) {
        benefitA.addEventListener("change", (e) => {
            _clearBenefitError();
            transactionsSection.classList.toggle("d-none", !e.target.checked);
            documentationSection.classList.toggle("d-none", !e.target.checked);
            benefitAFields.forEach((el) => {
                el.parentElement.classList.toggle("d-none", !e.target.checked);
            });
        });
    }
    if (benefitB) {
        benefitB.addEventListener("change", () => {
            _clearBenefitError();
        });
    }
}

function _clearBenefitError() {
    const errorEl = document.getElementById("benefit-selection-error");
    if (errorEl) {
        errorEl.remove();
    }
}

/**
 * Validate at least one benefit (A or B) is selected.
 */
function _validateBenefitSelection() {
    const benefitA = document.querySelector(
        '[name="benefit_a_oop_losses"]:checked'
    );
    const benefitB = document.querySelector(
        '[name="benefit_b_pro_rata"]:checked'
    );

    if (!benefitA && !benefitB) {
        _clearBenefitError();

        const header = document.querySelector(".benefitAHeader");
        if (header) {
            const errorDiv = document.createElement("div");
            errorDiv.id = "benefit-selection-error";
            errorDiv.className = "alert alert-danger mt-2";
            errorDiv.textContent =
                "Please select at least one benefit (A and/or B) to submit your claim.";
            header.parentNode.insertBefore(errorDiv, header);
            errorDiv.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return false;
    }
    return true;
}

/**
 * If Benefit A is selected, total amount is required and
 * documentation upload should be flagged.
 */
function _validateBenefitARequirements(claim) {
    const benefitA = document.querySelector(
        '[name="benefit_a_oop_losses"]:checked'
    );
    const fileList = document.querySelector('#file-list');


    if (benefitA) {
    
        // ensure at least one transaction is added
        if (_isEmptyTransactions(claim)) {
            modal('Error', 'Please add at least one transaction to your claim.');
            return false;
        }

        // ensure at least one document is uploaded
        if (fileList.children.length === 0) {
            modal('Error', 'Please upload at least one document to your claim.');
            return false;
        }

        // calculate total oop amount claimed
        claim.data.fields["Total amount for this category ($)"] = claim.inputs.transactions.filter(input => input.name.startsWith("expense_amount")).reduce((accumulator, currentValue) => !Number.isNaN(currentValue.value) ? accumulator + Number(currentValue.value) : accumulator, 0);
    }

    return true;
}

function _isEmptyTransactions(claim) {
    const transactions = Object.values(claim.data.transactions);
    const transactionInputs = Object.values(claim.inputs.transactions);
    return transactions.length === 0 || transactionInputs.length === 0 || transactions.some(transaction => Object.values(transaction).some(value => value === null || value === undefined || value === '')) || transactionInputs.some(input => input.value === null || input.value === undefined || input.value === '');
}

/**
 * Show/hide the alternate email fields based on the checkbox, and wire up
 * real-time "does not match" feedback between the two email inputs.
 */
function _initAlternateEmailToggle() {
    const checkbox = document.querySelector('[name="has_alternate_email"]');
    const emailFields = document.querySelectorAll(".toggle-has_alternate_email");

    if (!checkbox) return;

    // Show or hide email field parent wrappers based on the current checked state.
    emailFields.forEach((el) => {
        el.parentElement.classList.toggle("d-none", !checkbox.checked);
    });

    // Show/hide on checkbox change; clear values and errors when hiding.
    checkbox.addEventListener("change", (e) => {
        const isChecked = e.target.checked;
        emailFields.forEach((el) => {
            el.parentElement.classList.toggle("d-none", !isChecked);
            if (!isChecked) {
                el.value = "";
                toggleFieldError(el, false);
            }
        });
    });

    // Real-time mismatch feedback (mirrors ConfirmEmailAddress "init" logic).
    const altEmail = document.getElementById("alternate_email_address");
    const confirmAltEmail = document.getElementById("confirm_alternate_email_address");

    if (altEmail && confirmAltEmail) {
        const handleChange = () => {
            if (
                confirmAltEmail.value !== "" &&
                altEmail.value.toLowerCase() !== confirmAltEmail.value.toLowerCase()
            ) {
                toggleFieldError(confirmAltEmail, true, "Does Not Match");
            } else {
                toggleFieldError(confirmAltEmail, false);
            }
        };
        altEmail.addEventListener("change", handleChange);
        confirmAltEmail.addEventListener("change", handleChange);
    }
}

/**
 * On submission, validate alternate email fields only when the checkbox is
 * checked. Ensures both fields are filled and their values match.
 */
function _validateAlternateEmail() {
    const checkbox = document.querySelector('[name="has_alternate_email"]:checked');

    // Fields are hidden — nothing to validate.
    if (!checkbox) return true;

    const altEmail = document.getElementById("alternate_email_address");
    const confirmAltEmail = document.getElementById("confirm_alternate_email_address");

    if (!altEmail || !confirmAltEmail) return true;

    if (!altEmail.value.trim()) {
        altEmail.focus();
        toggleFieldError(altEmail, true, "Required");
        return false;
    }

    if (!confirmAltEmail.value.trim()) {
        confirmAltEmail.focus();
        toggleFieldError(confirmAltEmail, true, "Required");
        return false;
    }

    if (altEmail.value.toLowerCase() !== confirmAltEmail.value.toLowerCase()) {
        toggleFieldError(confirmAltEmail, true, "Does Not Match");
        confirmAltEmail.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
    }

    // Clear any lingering error state on a valid submission.
    toggleFieldError(confirmAltEmail, false);
    return true;
}
